import { type QueryObserverResult, type RefetchOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { type Department, useAuth } from "@/contexts/AuthProvider";
import { useDraftMode } from "@/contexts/DraftModeContext";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useNotificationsContext } from "@/contexts/NotificationsContext";
import { useRequestsContext } from "@/contexts/RequestsContext";
import { useWeekIndex } from "@/contexts/WeekIndexProvider";
import type { ScheduleInvalidation } from "@/hooks/scheduleCacheWrites";
import { useOnReactClientInactive } from "@/hooks/useOnReactClientInactive";
import { backendService } from "@/services/backend.service";
import { localStorageService } from "@/services/LocalStorageService";
import { trackErrorEvent } from "@/utils/analytics";
import type { GroupedActivityLogChangeResult, InHouseRefreshOptions, StoredGroupedActivityLog } from "@/utils/models";
import { holidayDepartmentKey } from "../team/Holidays/holidayQueryKeys";
import { getGroupedActivityLogClientTimestamps, getRefreshOptionsByGroupedActivityLog } from "./utils";

const singleSecond = 1000;

type OperationLabels = "refreshDepartmentData" | "refreshNursesData" | "refreshRequests" | "fetchNotifications" | "refreshWageBreakdown";

/**
 * Core global refresh logic that can be used without hooks
 * This is used internally by usePerformGlobalRefresh and can be used by providers
 */
export const performGlobalRefreshCore = async ({
	afterScheduleWrite,
	refreshNursesData,
	refreshRequests,
	fetchNotifications,
	refreshWageBreakdown,
	chosenDepartmentId,
	timezone,
	userId,
	refreshOptions,
	refetchRefreshStatus,
}: {
	afterScheduleWrite: (target: ScheduleInvalidation) => Promise<void>;
	refreshNursesData: (department_id: string) => Promise<void>;
	refreshRequests: () => Promise<void>;
	fetchNotifications: () => Promise<void>;
	refreshWageBreakdown: () => Promise<void>;
	chosenDepartmentId: string;
	timezone: string;
	userId: string;
	refreshOptions?: InHouseRefreshOptions;
	refetchRefreshStatus?: (options?: RefetchOptions) => Promise<QueryObserverResult<GroupedActivityLogChangeResult, Error>>;
}): Promise<void> => {
	const stamp = DateTime.now().setZone(timezone);
	const operations: {
		label: OperationLabels;
		specificOperation: () => Promise<void>;
		avoidRefresh: boolean;
	}[] = [
		{
			label: "refreshDepartmentData",
			specificOperation: async () => await afterScheduleWrite({ allWeeks: true }),
			avoidRefresh: refreshOptions?.avoidRefreshFor?.departmentData ?? false,
		},
		{
			label: "refreshNursesData",
			specificOperation: async () => await refreshNursesData(chosenDepartmentId),
			avoidRefresh: refreshOptions?.avoidRefreshFor?.nursesData ?? false,
		},
		{
			label: "refreshRequests",
			specificOperation: async () => await refreshRequests(),
			avoidRefresh: refreshOptions?.avoidRefreshFor?.requestsData ?? false,
		},
		{
			label: "fetchNotifications",
			specificOperation: async () => await fetchNotifications(),
			avoidRefresh: refreshOptions?.avoidRefreshFor?.notificationsData ?? false,
		},
		{
			label: "refreshWageBreakdown",
			specificOperation: async () => await refreshWageBreakdown(),
			avoidRefresh: refreshOptions?.avoidRefreshFor?.wageData ?? false,
		},
	];
	const errors: OperationLabels[] = [];
	const filteredOps = operations.filter((op) => !op.avoidRefresh);
	await Promise.allSettled(filteredOps.map(async (op) => await op.specificOperation())).then((results) => {
		results.forEach((result, index) => {
			if (result.status !== "fulfilled") {
				errors.push(filteredOps[index].label);
			}
		});
	});

	const previousStoredGroupedData = localStorageService.getClientGroupedRefreshData(userId);
	if (previousStoredGroupedData) {
		localStorageService.setClientGroupedRefreshData(
			{
				departmentData: {
					...previousStoredGroupedData.departmentData,
					lastChange:
						refreshOptions?.avoidRefreshFor.departmentData || errors.includes("refreshDepartmentData")
							? previousStoredGroupedData.departmentData.lastChange
							: stamp,
				},
				notifications: {
					...previousStoredGroupedData.notifications,
					lastChange:
						refreshOptions?.avoidRefreshFor.notificationsData || errors.includes("fetchNotifications")
							? previousStoredGroupedData.notifications.lastChange
							: stamp,
				},
				requests: {
					...previousStoredGroupedData.requests,
					lastChange:
						refreshOptions?.avoidRefreshFor.requestsData || errors.includes("refreshRequests") ? previousStoredGroupedData.requests.lastChange : stamp,
				},
				nurses: {
					...previousStoredGroupedData.nurses,
					lastChange: refreshOptions?.avoidRefreshFor.nursesData || errors.includes("refreshNursesData") ? previousStoredGroupedData.nurses.lastChange : stamp,
				},
				wage: {
					...previousStoredGroupedData.wage,
					lastChange: refreshOptions?.avoidRefreshFor.wageData || errors.includes("refreshWageBreakdown") ? previousStoredGroupedData.wage.lastChange : stamp,
				},
			},
			userId,
		);
	}
	await refetchRefreshStatus?.();
};

/**
 * Custom hook to perform a global refresh of the system data.
 * @param refetchRefreshStatus - Optional function to refetch the refresh status after performing the global refresh.
 * @param refreshOptions - Optional options to control which data should be refreshed or avoided during the global refresh.
 */
export const usePerformGlobalRefresh = ({
	refetchRefreshStatus,
}: {
	refetchRefreshStatus?: (options?: RefetchOptions) => Promise<QueryObserverResult<GroupedActivityLogChangeResult, Error>>;
}) => {
	const { chosenDepartment, timezone, afterScheduleWrite, refreshDepartmentWageData, refreshNursesData } = useHospitalData();
	const { refreshRequests } = useRequestsContext();
	const { fetchNotifications } = useNotificationsContext();
	const { isDraftMode } = useDraftMode();
	const {
		userInfo: { id: userId },
	} = useAuth();
	const queryClient = useQueryClient();
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const refreshWageBreakdown = useCallback(async () => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: ["shift-wage-breakdown"] }),
			queryClient.invalidateQueries({ queryKey: ["pto-costs"] }),
			queryClient.invalidateQueries({ queryKey: ["nurse-overtime"] }),
			queryClient.invalidateQueries({ queryKey: ["day-expenses"] }),
			// `department-wage-data` is enabled:false, so use the provider-owned refetch directly to update wageData on Day + Week views.
			refreshDepartmentWageData(),
		]);
	}, [queryClient, refreshDepartmentWageData]);

	const performGlobalRefresh = useCallback(
		async (refreshOptions?: InHouseRefreshOptions) => {
			// No department yet while the tab mints the token for the one in the URL: every refresh would 403.
			if (isDraftMode || !chosenDepartment.department_id) {
				return;
			}
			setIsLoading(true);
			await performGlobalRefreshCore({
				afterScheduleWrite,
				refreshNursesData,
				refreshRequests,
				fetchNotifications,
				refreshWageBreakdown,
				chosenDepartmentId: chosenDepartment.department_id,
				timezone,
				refreshOptions,
				refetchRefreshStatus,
				userId,
			});
			// Drop any cleared Annie open-screen approval rows after a resolving action (e.g. approve via Annie).
			await queryClient.invalidateQueries({ queryKey: ["annie-recommendations"] });
			// Holidays reload with the weeks, so one another admin applied shows with its shifts.
			if (!refreshOptions?.avoidRefreshFor?.departmentData) {
				await queryClient.invalidateQueries({ queryKey: holidayDepartmentKey(chosenDepartment.department_id) });
			}
			setIsLoading(false);
		},
		[
			chosenDepartment.department_id,
			timezone,
			afterScheduleWrite,
			refreshNursesData,
			refreshRequests,
			fetchNotifications,
			refreshWageBreakdown,
			refetchRefreshStatus,
			isDraftMode,
			userId,
			queryClient,
		],
	);
	return { performGlobalRefresh, isLoading };
};

export const usePollRefreshStatus = ({ disableHook }: { disableHook: boolean }) => {
	const { chosenDepartment } = useHospitalData();
	const { isDraftMode } = useDraftMode();
	const {
		userInfo: { id: userId },
	} = useAuth();

	return useQuery<GroupedActivityLogChangeResult>({
		// Keyed by department so a switch fetches the new one at once and never shows the previous one's counts.
		queryKey: ["activityLogLastChange", chosenDepartment.department_id],
		queryFn: async () => {
			const groupedClientStoredTimestamp = localStorageService.getClientGroupedRefreshData(userId);
			const groupedTimestamps = getGroupedActivityLogClientTimestamps(groupedClientStoredTimestamp);
			if (!groupedTimestamps) {
				const groupedResult = localStorageService.resetClientGroupedRefreshData(userId);
				return {
					departmentData: {
						changeCount: 0,
						latestChangeTime: groupedResult.departmentData.lastChange,
					},
					notifications: {
						changeCount: 0,
						latestChangeTime: groupedResult.notifications.lastChange,
					},
					requests: {
						changeCount: 0,
						latestChangeTime: groupedResult.requests.lastChange,
					},
					nurses: {
						changeCount: 0,
						latestChangeTime: groupedResult.nurses.lastChange,
					},
					wage: {
						changeCount: 0,
						latestChangeTime: groupedResult.wage.lastChange,
					},
				} as GroupedActivityLogChangeResult;
			}
			try {
				const result = await backendService.getRefreshStatusV2(chosenDepartment.department_id, groupedTimestamps);
				if (result.status === 200) return result.data;
			} catch (e) {
				trackErrorEvent("error_fetching_refresh_status", e, {
					groupedTimestamps,
					departmentId: chosenDepartment?.department_id,
				});
			}
			return {
				departmentData: { changeCount: 0 },
				notifications: { changeCount: 0 },
				requests: { changeCount: 0 },
				nurses: { changeCount: 0 },
				wage: { changeCount: 0 },
			} as GroupedActivityLogChangeResult;
		},
		throwOnError: false,
		refetchInterval: singleSecond * 20, // Poll every x seconds
		refetchIntervalInBackground: false, // Do not poll in the background
		refetchOnWindowFocus: true,
		staleTime: singleSecond * 30, // Data is fresh for x seconds
		// No department yet while the tab mints the token for the one in the URL — polling then would 403.
		enabled: !disableHook && !!chosenDepartment.department_id && !isDraftMode,
	});
};

const refreshBlacklistedPaths = ["/login", "/reset-password", "/404", "/add-nurse", "/nurse"];

const REFRESH_EVERYTHING: InHouseRefreshOptions = {
	avoidRefreshFor: {
		requestsData: false,
		departmentData: false,
		nursesData: false,
		notificationsData: false,
		wageData: false,
	},
};

/** Whether `pathname` is under one of the user's departments other than `shownDepartmentId`. */
export const isPathInAnotherDepartment = (pathname: string, shownDepartmentId: string, userDepartments: Department[] | undefined): boolean => {
	const [, firstSegment] = pathname.split("/");
	// DEFAULT: before the user info has loaded there is no department to be changing to.
	return firstSegment !== shownDepartmentId && (userDepartments ?? []).some((department) => department.department_id === firstSegment);
};

/**
 * Hook to register auto-fetch of system data based on URL path changes and week index changes.
 * @param timeToInitialEnableMs - Time in milliseconds to wait before enabling the auto-fetch after the component mounts.
 * @param autoFetchTimeoutMs - Timeout in milliseconds to check for inactivity before performing a global refresh.
 * @param refetchRefreshStatus - Optional function to refetch the refresh status after performing the global refresh.
 * @param fetchInAccordanceToStatus - Whether to fetch data in accordance with the refresh status - default is false which fetches only requests
 * @param lastFetchedRefreshStatusData - The current refresh status data.
 */
export const useRegisterAutoFetchSystemData = ({
	timeToInitialEnableMs = 5000,
	autoFetchTimeoutMs = singleSecond * 30,
	refetchRefreshStatus,
	lastFetchedRefreshStatusData,
}: {
	timeToInitialEnableMs?: number;
	autoFetchTimeoutMs?: number;
	refetchRefreshStatus?: (options?: RefetchOptions) => Promise<QueryObserverResult<GroupedActivityLogChangeResult, Error>>;
	lastFetchedRefreshStatusData: GroupedActivityLogChangeResult | undefined;
}) => {
	const { chosenDepartment, timezone } = useHospitalData();
	const location = useLocation();
	const { weekIndex } = useWeekIndex();
	const { isDraftMode } = useDraftMode();
	const {
		userInfo: { id: userId, departments: userDepartments },
	} = useAuth();

	const enabledRef = useRef(false);
	// A refresh skipped while the department changed, run once the new department is on screen.
	const refreshAfterDepartmentChangeRef = useRef(false);
	const { performGlobalRefresh, isLoading } = usePerformGlobalRefresh({ refetchRefreshStatus });

	const handlePerformGlobalRefresh = useCallback(async () => {
		if (enabledRef.current && !isDraftMode) {
			let refreshStatusData: StoredGroupedActivityLog | null = localStorageService.getClientGroupedRefreshData(userId);
			if (!refreshStatusData) {
				refreshStatusData = localStorageService.resetClientGroupedRefreshData(userId);
			}
			const groupedTimestamps = getGroupedActivityLogClientTimestamps(refreshStatusData);
			if (!groupedTimestamps) {
				trackErrorEvent(
					"oldest_refresh_timestamp_not_found",
					{},
					{
						refreshStatusData,
						departmentId: chosenDepartment?.department_id,
						timezone,
						groupedTimestamps,
					},
				);
				return;
			}
			const calculatedOptions = lastFetchedRefreshStatusData ? getRefreshOptionsByGroupedActivityLog(lastFetchedRefreshStatusData) : null;
			const refreshOptions = calculatedOptions ?? REFRESH_EVERYTHING;
			await performGlobalRefresh(refreshOptions);
		}
	}, [performGlobalRefresh, timezone, chosenDepartment?.department_id, isDraftMode, lastFetchedRefreshStatusData, userId]);

	useEffect(() => {
		setTimeout(() => {
			enabledRef.current = true;
		}, timeToInitialEnableMs);
	}, [timeToInitialEnableMs]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: we want to only trigger on weekIndex and location.pathname changes
	useEffect(() => {
		if (!enabledRef.current) {
			return;
		}
		// A path in another department means the department is changing. Refreshing the one still on screen
		// would race the new department's token — whose adoption loads everything for it anyway — and 403.
		if (isPathInAnotherDepartment(location.pathname, chosenDepartment.department_id, userDepartments)) {
			refreshAfterDepartmentChangeRef.current = true;
			return;
		}
		if (!refreshBlacklistedPaths.some((blacklistedPath) => location.pathname.startsWith(blacklistedPath))) {
			handlePerformGlobalRefresh();
		}
	}, [weekIndex, location.pathname]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: runs once per department change, for the refresh the change skipped
	useEffect(() => {
		if (!refreshAfterDepartmentChangeRef.current) return;
		refreshAfterDepartmentChangeRef.current = false;
		// Everything, not what the status counts say: the counts on hand may be a cached poll from an
		// earlier visit to this department, and would skip categories that changed since.
		void performGlobalRefresh(REFRESH_EVERYTHING);
	}, [chosenDepartment.department_id]);

	const onInactiveCallback = useCallback(async () => {
		if (enabledRef.current) {
			await handlePerformGlobalRefresh();
		}
	}, [handlePerformGlobalRefresh]);

	useOnReactClientInactive({
		timeoutMs: autoFetchTimeoutMs,
		onInactive: onInactiveCallback,
		hookDisabled: !enabledRef.current || !chosenDepartment.department_id || isDraftMode,
	});

	return {
		isLoading,
	};
};
